import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Play, Check, BookOpen, Clock, X as XIcon } from 'lucide-react';
import styles from './AIExamIntroPage.module.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../utils/translationHelpers';
import { SettingsDropdown } from '../SettingsDropdown';

export interface AIExamIntroPageProps {
  isUnlocked: boolean;
  // Locked
  completedPracticeCount: number;
  nextUnlockThreshold: number;
  // Unlocked
  lastScoreOut50: number | null;
  attemptCount: number;
  bestScoreOut50: number | null;
  worstScoreOut50: number | null;
  avgScoreOut50: number | null;
  weakTopics: Array<{ id: string; name: string; accuracy: number }>;
  showBreakdownBlank: boolean;
  onStartExam: () => void;
  ctaDisabled: boolean;
}

export function AIExamIntroPage({
  isUnlocked,
  completedPracticeCount,
  nextUnlockThreshold,
  lastScoreOut50,
  attemptCount,
  bestScoreOut50,
  worstScoreOut50,
  avgScoreOut50,
  weakTopics,
  showBreakdownBlank,
  onStartExam,
  ctaDisabled,
}: AIExamIntroPageProps) {
  const { t_nested } = useLanguage();
  const navigate = useNavigate();
  const backLabel = getTranslation(t_nested, 'aria.backToDashboard', 'Back to dashboard');
  const weakAreasListLabel = getTranslation(
    t_nested,
    'aiExamPage.weakAreasCarousel',
    'Weakest areas, swipe sideways to see all'
  );
  const topWeakTopics = weakTopics.slice(0, 3);

  if (!isUnlocked) {
    return (
      <div className={`${styles.page} practice-main-page ai-exam-intro-page safe-top`}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate('/dashboard')}
            aria-label={backLabel}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className={styles.topBarHeadings}>
            <h1 className={styles.pageTitle}>{t_nested('aiExamPage.title')}</h1>
            <p className={styles.pageMeta}>
              {completedPracticeCount} / {nextUnlockThreshold} · {t_nested('aiExamPage.progressToUnlock')}
            </p>
          </div>
          <div className={styles.topBarSettings}>
            <SettingsDropdown />
          </div>
        </div>

        <div className={styles.lockedContent}>
          {/* Icon row */}
          <div className={styles.iconRow}>
            <div className={styles.iconBadge}>
              <Lock size={24} className={styles.iconBadgeIcon} />
            </div>
            <div className={styles.iconTextGroup}>
              <p className={styles.iconLead}>{t_nested('aiExamPage.personalized')}</p>
            </div>
          </div>

          {/* Progress section */}
          <div className={styles.progressSection}>
            <div className={styles.progressHeader}>
              <span className={styles.progressLabel}>{t_nested('aiExamPage.progressToUnlock')}</span>
              <span className={styles.progressCount}>
                {completedPracticeCount} / {nextUnlockThreshold}
              </span>
            </div>
            <div className={styles.progressBars}>
              <div className={styles.progressBarTrack}>
                <div
                  className={styles.progressBarFillNavy}
                  style={{ width: `${(completedPracticeCount / nextUnlockThreshold) * 100}%` }}
                />
              </div>
              <div className={styles.progressBarTrack}>
                <div
                  className={styles.progressBarFillGold}
                  style={{ width: `${(completedPracticeCount / nextUnlockThreshold) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Benefits card */}
          <div className={styles.benefitsCard}>
            <h2 className={styles.benefitsTitle}>{t_nested('aiExamPage.afterUnlock')}</h2>
            <ul className={styles.benefitsList}>
              <li className={styles.benefitItem}>
                <span className={styles.benefitArrow} aria-hidden="true">&#8594;</span>
                {t_nested('aiExamPage.benefit1')}
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.benefitArrow} aria-hidden="true">&#8594;</span>
                {t_nested('aiExamPage.benefit2')}
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.benefitArrow} aria-hidden="true">&#8594;</span>
                {t_nested('aiExamPage.benefit3')}
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.benefitArrow} aria-hidden="true">&#8594;</span>
                {t_nested('aiExamPage.benefit4')}
              </li>
            </ul>
          </div>

        </div>
      </div>
    );
  }

  // Unlocked state
  const isPassed = lastScoreOut50 != null && lastScoreOut50 >= 44;
  const hasBreakdownData =
    bestScoreOut50 != null && worstScoreOut50 != null && avgScoreOut50 != null;

  return (
    <div className={`${styles.page} practice-main-page ai-exam-intro-page safe-top`}>
      <div className={styles.topBar}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate('/dashboard')}
          aria-label={backLabel}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className={styles.topBarHeadings}>
          <h1 className={styles.pageTitle}>{t_nested('aiExamPage.title')}</h1>
          <p className={styles.pageMeta}>{t_nested('aiExamPage.readyToStart')}</p>
        </div>
        <div className={styles.topBarSettings}>
          <SettingsDropdown />
        </div>
      </div>

      <div className={styles.unlockedContent}>
        {/* Unlocked badge */}
        <div className={styles.unlockedBadge}>
          <Check size={12} className={styles.unlockedBadgeIcon} aria-hidden="true" />
          <span className={styles.unlockedBadgeText}>{t_nested('aiExamPage.unlocked')}</span>
        </div>

        {/* Hero section */}
        <div className={styles.heroSection}>
          <h1 className={styles.heroTitle}>
            {t_nested('aiExamPage.examTitlePart1')}{' '}
            <span className={styles.heroTitleAccent}>{t_nested('aiExamPage.examTitlePart2')}</span>
          </h1>

          <div className={styles.heroBadges}>
            <div className={styles.heroBadge}>
              <BookOpen size={12} className={styles.heroBadgeIcon} aria-hidden="true" />
              {t_nested('aiExamPage.badge50Questions')}
            </div>
            <div className={styles.heroBadge}>
              <Clock size={12} className={styles.heroBadgeIcon} aria-hidden="true" />
              {t_nested('aiExamPage.badge30Minutes')}
            </div>
            <div className={styles.heroBadge}>
              {t_nested('aiExamPage.passThreshold')}
            </div>
          </div>

          <button
            type="button"
            className={styles.heroCtaButton}
            onClick={onStartExam}
            disabled={ctaDisabled}
          >
            <Play size={16} className={styles.heroCtaIcon} aria-hidden="true" />
            {t_nested('aiExamPage.startCta')}
          </button>
        </div>

        {/* Score card — only shown when there is a previous score */}
        {lastScoreOut50 != null && (
          <div className={styles.scoreCard}>
            <div className={styles.scoreCardTop}>
              <div className={styles.scoreLeft}>
                <span className={styles.scoreCardLabel}>{t_nested('aiExamPage.currentScore')}</span>
                <span className={styles.scoreValue}>
                  {Math.round((lastScoreOut50 / 50) * 100)}%
                </span>
              </div>

              <div className={styles.scoreRight}>
                {isPassed ? (
                  <div className={styles.passedBadge}>
                    <Check size={12} className={styles.passedBadgeIcon} aria-hidden="true" />
                    <span className={styles.passedBadgeText}>
                      {t_nested('aiExamPage.passed')} &middot; {lastScoreOut50}/50
                    </span>
                  </div>
                ) : (
                  <div className={styles.failedBadge}>
                    <XIcon size={12} className={styles.failedBadgeIcon} aria-hidden="true" />
                    <span className={styles.failedBadgeText}>
                      {t_nested('aiExamPage.notPassed')} &middot; {lastScoreOut50}/50
                    </span>
                  </div>
                )}
                <span className={styles.attemptCount}>{attemptCount} {t_nested('aiExamPage.attempts')}</span>
              </div>
            </div>

            {/* Score breakdown */}
            {hasBreakdownData && (
              <div className={styles.scoreBreakdown}>
                <div className={styles.scoreBreakdownCell}>
                  <span className={`${styles.breakdownNumber} ${styles.navy}`}>
                    {bestScoreOut50}
                  </span>
                  <span className={styles.breakdownCellLabel}>{t_nested('aiExamPage.best')}</span>
                </div>
                <div className={styles.scoreBreakdownCell}>
                  <span className={`${styles.breakdownNumber} ${styles.gold}`}>
                    {worstScoreOut50}
                  </span>
                  <span className={styles.breakdownCellLabel}>{t_nested('aiExamPage.worst')}</span>
                </div>
                <div className={styles.scoreBreakdownCell}>
                  <span className={`${styles.breakdownNumber} ${styles.green}`}>
                    {avgScoreOut50}
                  </span>
                  <span className={styles.breakdownCellLabel}>{t_nested('aiExamPage.average')}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Weak topics section */}
        <div className={styles.weakTopicsSection}>
          <h2 className={styles.weakTopicsTitle}>
            {t_nested('aiExamPage.focusedOnWeakSpots')}
          </h2>

          {topWeakTopics.length > 0 ? (
            <div
              className={styles.weakPillTrack}
              role="list"
              aria-label={weakAreasListLabel}
            >
              {topWeakTopics.map((topic, index) => (
                <div
                  key={topic.id}
                  className={styles.weakPill}
                  role="listitem"
                >
                  <div className={styles.weakPillTop}>
                    <span className={styles.weakPillTitle}>{topic.name}</span>
                    {!showBreakdownBlank && (
                      <span className={styles.weakPillRank} aria-hidden>
                        #{index + 1}
                      </span>
                    )}
                  </div>
                  {!showBreakdownBlank && (
                    <div className={styles.weakPillProgress}>
                      <div className={styles.weakPillBar}>
                        <div
                          className={styles.weakPillFill}
                          style={{ width: `${Math.min(100, Math.max(0, topic.accuracy))}%` }}
                        />
                      </div>
                      <span className={styles.weakPillPct}>{Math.round(topic.accuracy)}%</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.weakTopicsBlank}>
              {t_nested('aiExamPage.takeTestsForBreakdown')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIExamIntroPage;
